"use client";

import React, { forwardRef, useCallback, useMemo } from "react";
import { VirtuosoGrid, VirtuosoGridProps, ContextProp } from "react-virtuoso";
import { SwiperItem } from "@/components/SwiperItem/MovieItemComponent";
import { toMovieItem } from "@/lib/movieMapper";
import { slugify } from "@/lib/slugify";
import { PersonContent } from "@/types/person";
import { Movie } from "@/types/movies";
import { SliderSkeleton } from "../pages/home/loading";

interface Props {
  data: PersonContent[];
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const gridComponents: NonNullable<
  VirtuosoGridProps<Movie, undefined>["components"]
> = {
  List: forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    function ListContainer({ style, children, ...props }, ref) {
      return (
        <div
          ref={ref}
          {...props}
          className="grid-container"
          style={style}
        >
          {children}
        </div>
      );
    }
  ),
  Item: forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    function GridItem({ children, ...props }, ref) {
      return (
        <div
          ref={ref}
          {...props}
          style={{ padding: "0.25rem", boxSizing: "border-box" }}
        >
          {children}
        </div>
      );
    }
  ),
};

export default function VirtuosoMainCard({
  data,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: Props) {
  const items = useMemo(
    (): Movie[] =>
      data.map((item) => ({
        uuid: item.unique_code,
        unique_code: item.unique_code,
        title_fa: item.title_fa,
        title_en: item.title_en,
        poster_url: item.poster_url || "/images/poster-placeholder.jpg",
        href: (() => { const s = slugify(item.title_en || item.title_fa); const suf = s ? `/${s}` : ""; return `/${item.content_type === "series" ? "s" : item.content_type}/${item.unique_code}${suf}`; })(),
        type: item.content_type,
        entity_type: item.entity_type ?? item.content_type,
        release_date: item.release_date ?? undefined,
        tags: item.tags ?? [],
        persons: item.persons ?? [],
        subtitles: item.subtitles ?? [],
        trailers: item.trailers ?? [],
        medias: item.medias ?? [],
        categories: item.categories ?? [],
        age_restriction: null,
        has_subtitles:
          Boolean(item.has_subtitles) || (item.subtitles?.length ?? 0) > 0 ? "true" : null,
        has_dubbing:
          Boolean(item.has_dubbing) || (item.dubs?.length ?? 0) > 0 ? "true" : null,
      })),
    [data]
  );

  type FooterComponent = React.ForwardRefExoticComponent<
    React.RefAttributes<HTMLDivElement>
  >;



  
  const footerComponent = useMemo<
    React.ComponentType<ContextProp<undefined>> | undefined
  >(() => {
    if (!isLoadingMore) {
      return undefined;
    }

    return function LoadingFooter() {
      return (
        <SliderSkeleton />
      );
    };
  }, [isLoadingMore]);

  const components = useMemo<
    NonNullable<VirtuosoGridProps<Movie, undefined>["components"]>
  >(
    () =>
      footerComponent
        ? {
          ...gridComponents,
          Footer: footerComponent,
        }
        : gridComponents,
    [footerComponent]
  );

  const handleEndReached = useCallback(() => {
    if (hasMore && !isLoadingMore && onLoadMore) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  if (!items.length) return null;

  return (
    <VirtuosoGrid
      style={{ width: "100%", height: "auto" }}
      totalCount={items.length}
      useWindowScroll
      components={components}
      itemContent={(index) => <SwiperItem {...toMovieItem(items[index])} />}
      overscan={300}
      endReached={handleEndReached}
    />
  );
}
